import { ComponentProps, useRef } from "react";

import { DragDropManager } from "@dnd-kit/abstract";
import {
  Feedback,
  KeyboardSensor,
  PointerActivationConstraints,
  PointerSensor,
} from "@dnd-kit/dom";
import { RestrictToWindow } from "@dnd-kit/dom/modifiers";
// Use debug plugin to help with dnd handling
import { Debug } from "@dnd-kit/dom/plugins/debug";
import { isSortable, isSortableOperation } from "@dnd-kit/dom/sortable";
import { DragDropProvider } from "@dnd-kit/react";

import { MoveResult, OnDragEndEvent, OnDragOverEvent } from "./dndHelpers";

/**
 * DndContext is a wrapper around the DndKit DragDropProvider component.
 */
type DndContextProps = Omit<
  ComponentProps<typeof DragDropProvider>,
  "onDragOver" | "onDragEnd"
> & {
  onDragOver?: (
    event: OnDragOverEvent,
    manager: DragDropManager<any, any>,
    setMoveResult: (moveResult: MoveResult) => void,
  ) => void;
  onDragEnd?: (
    event: OnDragEndEvent,
    manager: DragDropManager<any, any>,
    setMoveResult: (moveResult: MoveResult) => void,
  ) => void;
  onDidMove?: (moveResult: MoveResult) => void;
};

export const DndContext = ({
  children,
  onDragOver,
  onDragEnd,
  onDidMove,
  ...rest
}: DndContextProps) => {
  const currResult = useRef<MoveResult | null>(null);

  const setMoveResult = (moveResult: MoveResult) => {
    currResult.current = moveResult;
  };

  return (
    <DragDropProvider
      plugins={(defaults) => [
        ...defaults,
        Feedback.configure({ feedback: "clone" }),
        // Debug,
      ]}
      onDragOver={(event, manager) => {
        if (!isSortableOperation(event.operation)) {
          return;
        }

        const { source, target } = event.operation;
        if (!source || !target) return;
        if (source.id === target.id) {
          event.preventDefault();
          return;
        }
        if (
          (source?.data?.disabled || target?.data?.disabled) &&
          source?.group === target?.group
        ) {
          event.preventDefault();
          return;
        }
        // dnd-kit's OptimisticSortingPlugin moves DOM nodes itself unless the
        // handler re-renders synchronously (e.g. useState). Updates that render
        // later (e.g. via the query cache) let it reparent a React-owned node
        // into another container, and React's later removeChild from the
        // original parent throws NotFoundError. Same-parent moves are safe, so
        // only block the plugin when the move would cross containers.
        if (
          isSortable(target) &&
          source.sortable.element?.parentElement !==
            target.sortable.element?.parentElement
        ) {
          event.preventDefault();
        }
        onDragOver?.(event, manager, setMoveResult);
      }}
      onDragEnd={(event, manager) => {
        if (!isSortableOperation(event.operation)) {
          return;
        }
        if (event.canceled) return;

        const { source, target } = event.operation;
        if (
          (source?.data?.disabled || target?.data?.disabled) &&
          source?.group === target?.group
        ) {
          return;
        }

        onDragEnd?.(event, manager, setMoveResult);

        const result = currResult.current;
        if (result) {
          onDidMove?.(result);
          currResult.current = null;
        }
      }}
      modifiers={(defaults) => [...defaults, RestrictToWindow]}
      sensors={[
        PointerSensor.configure({
          activationConstraints: [
            new PointerActivationConstraints.Distance({ value: 5 }),
          ],
        }),
        KeyboardSensor,
      ]}
      {...rest}
    >
      {children}
    </DragDropProvider>
  );
};
